// Generated by Claude (Claude Code). Conversation: https://claude.ai/code/session_01EL8pTjS9jd27w4Xu3veGVW
import { useEffect } from "react";
import { nanoid } from "nanoid";
import { getCsrfToken } from "@/lib/queryClient";
import { createActiveTimeTracker } from "@/lib/active-time-tracker";

const HEARTBEAT_URL = "/api/time-tracking/heartbeat";
const HEARTBEAT_INTERVAL_MS = 60_000;
const IDLE_TIMEOUT_MS = 2 * 60_000;
const ACTIVITY_THROTTLE_MS = 1_000;

/**
 * Passively tracks active time on a work order detail page and reports it to the
 * server. Nothing is shown to the user.
 *
 * Pass null to turn tracking off (for example, until the work order has loaded,
 * or for roles that are not tracked).
 *
 * Every report carries the cumulative active time for this page mount under one
 * session token, so the server keeps the largest value it has seen and a lost or
 * late request costs nothing.
 *
 * Reports use fetch with keepalive and the CSRF header. sendBeacon cannot send
 * headers, so the server's CSRF check would refuse it.
 */
export function useActiveTimeTracker(workOrderId: number | null): void {
  useEffect(() => {
    if (!workOrderId) return;

    const sessionToken = nanoid();
    const tracker = createActiveTimeTracker(Date.now, {
      visible: !document.hidden,
      focused: document.hasFocus(),
    });

    // Cached for the page lifetime by getCsrfToken. Kept here too so a report sent
    // while the page is closing does not have to wait on a fetch.
    let csrfToken = "";
    function loadCsrfToken() {
      getCsrfToken().then((token) => { csrfToken = token; }).catch(() => {});
    }
    loadCsrfToken();

    // The largest total the server has answered for. It moves only on a response, so
    // a report lost to the network or a server error is sent again on the next tick
    // even when the user has gone idle and the total has stopped growing.
    let settledMs = 0;

    function report() {
      const activeMs = tracker.activeMs();
      if (activeMs <= settledMs) return;
      if (!csrfToken) {
        loadCsrfToken();
        return;
      }
      fetch(HEARTBEAT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-csrf-token": csrfToken },
        credentials: "include",
        keepalive: true,
        body: JSON.stringify({ sessionToken, workOrderId, activeMs }),
      }).then((res) => {
        // 2xx: stored. 4xx: refused, and resending the same total would be refused
        // again. 5xx: leave it for the next tick.
        if (res.status < 500) settledMs = Math.max(settledMs, activeMs);
      }).catch(() => {
        // Network failure. The next tick sends the same cumulative total again.
      });
    }

    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    function restartIdleTimer() {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => tracker.setIdle(true), IDLE_TIMEOUT_MS);
    }

    let lastActivityAt = 0;
    function handleUserActivity() {
      const now = Date.now();
      if (now - lastActivityAt < ACTIVITY_THROTTLE_MS) return;
      lastActivityAt = now;
      tracker.setIdle(false);
      restartIdleTimer();
    }

    function handleVisibilityChange() {
      tracker.setVisible(!document.hidden);
      if (document.hidden) report();
    }

    function handleWindowBlur() {
      // Clicking into an embedded document (a PDF preview, for example) blurs the
      // window while the user is still on this page. The focused iframe only shows
      // up as document.activeElement after the blur event, so check on the next tick.
      setTimeout(() => {
        const stillHere = document.activeElement?.tagName === "IFRAME" && !document.hidden;
        tracker.setFocused(stillHere);
      }, 0);
    }

    function handleWindowFocus() {
      tracker.setFocused(true);
      tracker.setIdle(false);
      restartIdleTimer();
    }

    function handlePageHide() {
      tracker.setVisible(false);
      report();
    }

    const activityEvents = ["mousemove", "keydown", "scroll", "touchstart", "pointerdown"] as const;
    for (const event of activityEvents) {
      document.addEventListener(event, handleUserActivity, { passive: true });
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("pagehide", handlePageHide);

    restartIdleTimer();
    const heartbeatTimer = setInterval(report, HEARTBEAT_INTERVAL_MS);

    return () => {
      clearInterval(heartbeatTimer);
      if (idleTimer) clearTimeout(idleTimer);
      for (const event of activityEvents) {
        document.removeEventListener(event, handleUserActivity);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("pagehide", handlePageHide);

      // Leaving the page inside the app (route change) or switching work orders.
      tracker.setVisible(false);
      report();
    };
  }, [workOrderId]);
}
